import React from "react";

/**
 * CircularAudioIcon — the mEYEtrainer voice-chat mark.
 *
 * Ring of 28 short bars around a small centre waveform. ONE bar is rendered TALLER than the rest
 * and swept clockwise around the ring by rotating the whole highlight group. The bar itself gets
 * a slow hue-rotate so it cycles cyan → violet → fuchsia while it travels.
 *
 * Pure SVG + CSS keyframes — no framer-motion needed, and it keeps rendering while the browser
 * throttles animation frames on backgrounded tabs, so the button always feels alive.
 */
const BAR_COUNT = 28;

export const CircularAudioIcon = ({ size = 40, className = "" }) => {
    // Static bars around the ring: fixed cyan → violet → fuchsia sweep so the whole thing reads
    // as a spectrum wheel even when nothing is animating.
    const bars = [];
    for (let i = 0; i < BAR_COUNT; i++) {
        const angle = (i / BAR_COUNT) * 360;
        // hue sweeps cyan (185) → violet (270) → fuchsia (300) → cyan
        const hue = 185 + (i / BAR_COUNT) * 130;
        bars.push(
            <rect
                key={i}
                x={-1.2}
                y={-36}
                width={2.4}
                height={9}
                rx={1.2}
                fill={`hsl(${hue}, 90%, 60%)`}
                transform={`rotate(${angle})`}
            />,
        );
    }

    return (
        <svg
            width={size}
            height={size}
            viewBox="-50 -50 100 100"
            className={className}
            aria-hidden="true"
        >
            {/* Outer glow ring (thin, subtle) */}
            <circle cx="0" cy="0" r="46" fill="none" stroke="url(#ring-grad)" strokeWidth="0.8" opacity="0.6" />
            <defs>
                <linearGradient id="ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#22d3ee" />
                    <stop offset="100%" stopColor="#d946ef" />
                </linearGradient>
            </defs>

            {/* Static equaliser ring */}
            <g>{bars}</g>

            {/* Rotating highlight bar — sits ON TOP of one of the ring bars and travels clockwise.
                Height is ~2× the ring bars, and hue shifts as it goes.  */}
            <g style={{ animation: "meye-spin 8s linear infinite", transformOrigin: "0 0" }}>
                <rect
                    x={-1.6}
                    y={-40}
                    width={3.2}
                    height={16}
                    rx={1.6}
                    style={{ animation: "meye-hue 6s linear infinite", fill: "#22d3ee" }}
                />
            </g>

            {/* Centre waveform pip */}
            <circle cx="0" cy="0" r="13" fill="rgba(0,0,0,0.65)" stroke="rgba(140, 200, 240, 0.35)" strokeWidth="0.5" />
            <g fill="url(#wave-grad)">
                <defs>
                    <linearGradient id="wave-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22d3ee" />
                        <stop offset="100%" stopColor="#d946ef" />
                    </linearGradient>
                </defs>
                <rect x={-5} y={-3} width={1.4} height={6} rx={0.7} />
                <rect x={-2.6} y={-5} width={1.4} height={10} rx={0.7} />
                <rect x={-0.2} y={-7} width={1.4} height={14} rx={0.7} />
                <rect x={2.2} y={-5} width={1.4} height={10} rx={0.7} />
                <rect x={4.6} y={-3} width={1.4} height={6} rx={0.7} />
            </g>
        </svg>
    );
};

export default CircularAudioIcon;
